import Box from '@mui/material/Box';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PortalCard from './PortalCard';
import type { CardMetadata } from '../../types/card';
import type { SetThumbnailFn } from '../../types/addCard';

interface Props {
  card: CardMetadata;
  onUpdate?: (id: string, updates: { title: string; summary: string; url: string }) => Promise<void>;
  onSetThumbnail?: SetThumbnailFn;
  onDelete?: (id: string) => Promise<void>;
}

export default function SortableCard({ card, onUpdate, onSetThumbnail, onDelete }: Props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `card-${card.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: 'relative',
        '&:hover .card-drag-handle': { opacity: 1 },
      }}
    >
      <Box
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="card-drag-handle"
        sx={{
          position: 'absolute',
          top: 6,
          left: 6,
          zIndex: 3,
          display: 'flex',
          cursor: 'grab',
          color: 'text.disabled',
          opacity: 0,
          transition: 'opacity 150ms ease-in-out',
          bgcolor: 'background.paper',
          borderRadius: '4px',
          p: '2px',
          '&:hover': { color: 'text.secondary' },
        }}
        aria-label={`Drag ${card.title}`}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
      <PortalCard card={card} onUpdate={onUpdate} onSetThumbnail={onSetThumbnail} onDelete={onDelete} />
    </Box>
  );
}
